import { useEffect, useMemo, useState } from 'react';
import { Box, Button, IconButton, Typography } from '@mui/material';
import { CalendarIcon, CalendarOutlinedActiveIcon, ArrowLeftIcon, PlusIcon, ArrowRightIcon } from '~/assets/icons';
import dayjs, { ManipulateType } from 'dayjs';
import { BaseHeaderTitle, BaseTab, BaseTabList } from '~/components/common';
import { useTranslation } from 'react-i18next';
import Calendar from '~/components/schedules/calendar/Calendar';
import { View, Views, ViewsProps } from 'react-big-calendar';
import logo from '~/assets/images/logo.svg';
import { IScheduleEvent } from '~/utils/interface/schedule';
import { BaseInput } from '~/components/common';
import { SearchIcon } from '~/assets/icons';
import classNames from 'classnames/bind';
import styles from './Calendar.module.scss';

type ViewType = View & ManipulateType;

const COMPANY_SCHEDULE_MODE = 0;
const MY_SCHEDULE_MODE = 1;

const views: ViewsProps = {
  month: true,
  week: true,
  day: true,
};

const cx = classNames.bind(styles);
const events: Array<IScheduleEvent> = [
  {
    name: 'American Airline-TX04818-5 Contact Makayla Dawn Desai Contact fäest Level AV Inc',
    start_date: dayjs().hour(11).minute(0).second(0).toDate(),
    end_date: dayjs().hour(11).minute(15).second(0).toDate(),
    // assignees: [
    //   {
    //     name: 'Mr.A',
    //     image_path: logo,
    //   },
    //   {
    //     name: 'Mr.B',
    //     image_path: logo,
    //   },
    //   {
    //     name: 'Mr.B',
    //     image_path: logo,
    //   },
    //   {
    //     name: 'Mr.B',
    //     image_path: logo,
    //   },
    // ],
    color: '#ecf6ff',
  },
  {
    name: 'Event 2',
    start_date: dayjs().add(1, 'days').startOf('date').toDate(),
    end_date: dayjs().add(1, 'days').endOf('date').toDate(),
    color: '#dff3e4',
    assignees: [
      {
        name: 'Mr.A',
        image_path: logo,
      },
    ],
  },
  {
    name: 'Event 3',
    start_date: dayjs().add(1, 'days').startOf('date').toDate(),
    end_date: dayjs().add(1, 'days').endOf('date').toDate(),
    color: '#ffe5e5',
    assignees: [
      {
        name: 'Mr.A',
        image_path: logo,
      },
    ],
  },
  {
    name: 'Event 4',
    start_date: dayjs().add(1, 'days').startOf('date').toDate(),
    end_date: dayjs().add(1, 'days').endOf('date').toDate(),
    color: '#dff3e4',
    assignees: [
      {
        name: 'Mr.A',
        image_path: logo,
      },
    ],
  },
  {
    name: 'Event 5',
    start_date: dayjs().add(1, 'days').hour(11).minute(0).second(0).toDate(),
    end_date: dayjs().add(1, 'days').hour(11).minute(15).second(0).toDate(),
    color: '#dff3e4',
    assignees: [
      {
        name: 'Mr.A',
        image_path: logo,
      },
    ],
  },
];

const Schedule = () => {
  const [scheduleMode, setScheduleMode] = useState<number>(COMPANY_SCHEDULE_MODE);
  const [view, setView] = useState<ViewType>(Views.MONTH);
  const [currentTime, setCurrentTime] = useState<Date>(dayjs().toDate());

  const { t } = useTranslation(['admin/calendar', 'admin/common']);

  useEffect(() => {
    setCurrentTime(dayjs().toDate());
  }, [scheduleMode, view]);

  const goPreviousTime = () => {
    const previousTime = dayjs(currentTime).subtract(1, view);
    setCurrentTime(previousTime.toDate());
  };

  const goNextTime = () => {
    const nextTime = dayjs(currentTime).add(1, view);
    setCurrentTime(nextTime.toDate());
  };

  const goToday = () => {
    const now = dayjs().toDate();
    setCurrentTime(now);
  };

  const handleChangeScheduleMode = (_event: React.ChangeEvent<{}>, newValue: number) => {
    setScheduleMode(newValue);
  };

  const handleViewChange = (_event: React.ChangeEvent<{}>, newValue: ViewType): void => {
    setView(newValue);
  };

  const timeValue = useMemo(() => {
    switch (view) {
      case Views.WEEK:
        const startOfWeek = dayjs(currentTime).startOf('week').add(1, 'day').format('dddd, MMMM DD');
        return startOfWeek;
      case Views.DAY:
        return dayjs(currentTime).format('dddd, MMMM DD');
      default:
        return dayjs(currentTime).format('MMMM YYYY');
    }
  }, [view, currentTime]);

  return (
    true ? "Calendar page" : <Box className='flex flex-col'>
      {/*<Box className='flex flex-row'>
        <BaseHeaderTitle title={t('title')} />
      </Box>*/}
      <Box className='mb-[20px] flex flex-col items-start lg:flex-row lg:items-center'>
        <Box className='flex flex-1 min-w-[220px]'>
          <Typography className='text-[20px]/[26.04px] text-[#42485B] font-medium' variant='h5'>
            <BaseHeaderTitle title={t('title')} />
          </Typography>

          <Box className='flex flex-row items-center'>
            <Box className='flex flex-row items-center lg:flex-row'>
              <Typography className='text-[20px]/[26.04px] mr-[10px] text-[#42485B] font-medium' variant='h5'>
                {timeValue}
              </Typography>
              <IconButton
                color='primary'
                className='pl-[6px] pr-[4.5px] py-[7.5px] rounded-l-lg rounded-r-none bg-[#007AFC] hover:bg-[#007AFC]'
                onClick={goPreviousTime}
              >
                <ArrowLeftIcon className='w-[13px] h-[13px]' />
              </IconButton>
              <IconButton
                color='primary'
                className='pl-[4.5px] pr-[6px] py-[7.5px] rounded-r-lg rounded-l-none bg-[#007AFC] hover:bg-[#007AFC]'
                onClick={goNextTime}
              >
                <ArrowRightIcon className='w-[13px] h-[13px]' />
              </IconButton>
            </Box>
            {/*<Button
              variant='outlined'
              className='text-[#797D8A] border border-[#E5EAEF] hover:border-[#E5EAEF] rounded-lg text-[14px]/[16.8px] font-medium ml-[8px] h-[28px] px-[9px] py-[5.5px]'
              onClick={goToday}
            >
              {t('today')}
            </Button>*/}
          </Box>
        </Box>
        <Box className='flex flex-row flex-1'>
          
        </Box>
        <Box className='flex flex-col items-start mt-[8px] lg:mt-0 lg:flex-row'>
          {/*<Box>
            <BaseTabList
              value={scheduleMode}
              onChange={handleChangeScheduleMode}
              centered
              TabIndicatorProps={{ style: { backgroundColor: 'transparent' } }}
            >
              <BaseTab
                icon={
                  scheduleMode === COMPANY_SCHEDULE_MODE ? (
                    <CalendarOutlinedActiveIcon className='w-[17px] h-[17px]' />
                  ) : (
                    <CalendarIcon className='w-[17px] h-[17px]' />
                  )
                }
                iconPosition='start'
                label={t('companySchedule')}
              />
              <BaseTab
                icon={
                  scheduleMode === MY_SCHEDULE_MODE ? (
                    <CalendarOutlinedActiveIcon className='w-[17px] h-[17px]' />
                  ) : (
                    <CalendarIcon className='w-[17px] h-[17px]' />
                  )
                }
                iconPosition='start'
                label={t('mySchedule')}
              />
            </BaseTabList>
          </Box>*/}
          <Box className={`mt-[8px] lg:ml-[8px] lg:mt-0`}>
            <BaseTabList
              value={view}
              onChange={handleViewChange}
              centered
              TabIndicatorProps={{ style: { backgroundColor: 'transparent' } }}
            >
              {Object.keys(views).map((view: string, index: number) => {
                return <BaseTab value={view} key={index} label={t(`datetime.${view}`, { ns: 'admin/common' })} />;
              })}
            </BaseTabList>
          </Box>
          <Box className='w-[525px] ml-[5px]'>
            <BaseInput
              className='search-input'
              placeholder={t('header.search')}
              iconStart={
                <Box>
                  <SearchIcon className='w-[16px]' />
                </Box>
              }
            />
          </Box>
        </Box>
      </Box>
      <Box className='overflow-auto'>
        <Calendar view={view} date={currentTime} events={events} />
      </Box>
    </Box>
  );
};

export default Schedule;
